import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertCircle, CheckCircle, FileText, Activity, Heart, Brain, Stethoscope, TrendingUp, Clock, Shield, Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HospiceEligibilityPage() {
  const [activeTab, setActiveTab] = useState('assess');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [diagnosisCategories, setDiagnosisCategories] = useState([]);
  const [lcdCriteria, setLcdCriteria] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [recentAssessments, setRecentAssessments] = useState([]);

  // Form state for new assessment
  const [formData, setFormData] = useState({
    patientId: '',
    referralId: '',
    primaryDiagnosis: '',
    secondaryDiagnoses: '',
    ppsScore: 50,
    karnofskyScore: '',
    adlDependencies: [],
    albumin: '',
    oxygenSaturation: '',
    weightLossPercent: '',
    hospitalizations: 0,
    symptoms: []
  });

  const adlOptions = ['Bathing', 'Dressing', 'Toileting', 'Transferring', 'Continence', 'Feeding'];
  const symptomOptions = ['Pain', 'Dyspnea', 'Nausea', 'Anxiety', 'Fatigue', 'Confusion', 'Anorexia', 'Edema'];

  useEffect(() => {
    fetchDiagnosisCategories();
    fetchStats();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchDiagnosisCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/phase36/eligibility/diagnosis-categories`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setDiagnosisCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch diagnosis categories:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/phase36/eligibility/stats`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchLCDCriteria = async (category) => {
    if (!category) return;
    setSelectedCategory(category);
    try {
      const res = await fetch(`${API_URL}/api/phase36/eligibility/lcd-criteria?diagnosis_category=${category}`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setLcdCriteria(data);
      }
    } catch (error) {
      console.error('Failed to fetch LCD criteria:', error);
    }
  };

  const runEligibilityAssessment = async () => {
    if (!formData.patientId || !formData.primaryDiagnosis) {
      toast.error('Patient ID and Primary Diagnosis are required');
      return;
    }

    setLoading(true);
    try {
      const clinicalData = {
        primary_diagnosis: formData.primaryDiagnosis,
        secondary_diagnoses: formData.secondaryDiagnoses ? formData.secondaryDiagnoses.split(',').map(s => s.trim()) : [],
        functional_status: {
          pps_score: parseInt(formData.ppsScore) || 50,
          karnofsky_score: formData.karnofskyScore ? parseInt(formData.karnofskyScore) : null,
          adl_dependencies: formData.adlDependencies
        },
        vital_signs: {
          oxygen_saturation: formData.oxygenSaturation || null
        },
        laboratory_values: {
          serum_albumin: formData.albumin || null
        },
        symptoms: formData.symptoms.map(s => ({ name: s, severity: 5 })),
        weight_change: {
          percent_change: formData.weightLossPercent ? parseFloat(formData.weightLossPercent) : 0,
          period_months: 6
        },
        hospitalizations_6_months: parseInt(formData.hospitalizations) || 0
      };

      const res = await fetch(`${API_URL}/api/phase36/eligibility/assess?patient_id=${formData.patientId}${formData.referralId ? `&referral_id=${formData.referralId}` : ''}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(clinicalData)
      });

      if (res.ok) {
        const data = await res.json();
        setAssessmentResult(data);
        toast.success('Eligibility assessment completed');
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Assessment failed');
      }
    } catch (error) {
      console.error('Assessment error:', error);
      toast.error('Failed to run assessment');
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadgeColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'high': return 'bg-red-500';
      case 'moderate-high': return 'bg-orange-500';
      case 'moderate': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6" data-testid="hospice-eligibility-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <Shield className="h-8 w-8 text-teal-600" />
            Hospice Eligibility Assessment
          </h1>
          <p className="text-slate-600 mt-2">LCD-compliant eligibility determination with audit-proof documentation</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Assessments</p>
                    <p className="text-2xl font-bold text-slate-800">{stats.total_assessments}</p>
                  </div>
                  <FileText className="h-8 w-8 text-teal-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Eligible</p>
                    <p className="text-2xl font-bold text-green-600">{stats.eligible_count}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Not Eligible</p>
                    <p className="text-2xl font-bold text-orange-600">{stats.not_eligible_count}</p>
                  </div>
                  <AlertCircle className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Eligibility Rate</p>
                    <p className="text-2xl font-bold text-teal-600">{stats.eligibility_rate}%</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-teal-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-sm">
            <TabsTrigger value="assess" data-testid="tab-assess">New Assessment</TabsTrigger>
            <TabsTrigger value="lcd" data-testid="tab-lcd">LCD Criteria</TabsTrigger>
            <TabsTrigger value="results" data-testid="tab-results">Results</TabsTrigger>
          </TabsList>

          {/* New Assessment Tab */}
          <TabsContent value="assess">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Stethoscope className="h-5 w-5 text-teal-600" />
                    Patient & Clinical Data
                  </CardTitle>
                  <CardDescription>Enter patient information and clinical findings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="patientId">Patient ID *</Label>
                      <Input
                        id="patientId"
                        data-testid="input-patient-id"
                        value={formData.patientId}
                        onChange={(e) => setFormData({...formData, patientId: e.target.value})}
                        placeholder="Enter patient ID"
                      />
                    </div>
                    <div>
                      <Label htmlFor="referralId">Referral ID (Optional)</Label>
                      <Input
                        id="referralId"
                        data-testid="input-referral-id"
                        value={formData.referralId}
                        onChange={(e) => setFormData({...formData, referralId: e.target.value})}
                        placeholder="REF-..."
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="primaryDiagnosis">Primary Diagnosis *</Label>
                    <Select
                      value={formData.primaryDiagnosis}
                      onValueChange={(value) => setFormData({...formData, primaryDiagnosis: value})}
                    >
                      <SelectTrigger data-testid="select-diagnosis">
                        <SelectValue placeholder="Select primary diagnosis category" />
                      </SelectTrigger>
                      <SelectContent>
                        {diagnosisCategories.map(cat => (
                          <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="secondaryDiagnoses">Secondary Diagnoses</Label>
                    <Textarea
                      id="secondaryDiagnoses"
                      data-testid="input-secondary"
                      value={formData.secondaryDiagnoses}
                      onChange={(e) => setFormData({...formData, secondaryDiagnoses: e.target.value})}
                      placeholder="Enter secondary diagnoses, comma separated"
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ppsScore">PPS Score (0-100)</Label>
                      <Input
                        id="ppsScore"
                        type="number"
                        data-testid="input-pps"
                        min={0}
                        max={100}
                        value={formData.ppsScore}
                        onChange={(e) => setFormData({...formData, ppsScore: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="karnofskyScore">Karnofsky Score</Label>
                      <Input
                        id="karnofskyScore"
                        type="number"
                        data-testid="input-karnofsky"
                        min={0}
                        max={100}
                        value={formData.karnofskyScore}
                        onChange={(e) => setFormData({...formData, karnofskyScore: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>ADL Dependencies</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {adlOptions.map(adl => (
                        <Badge
                          key={adl}
                          variant={formData.adlDependencies.includes(adl) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => {
                            const deps = formData.adlDependencies.includes(adl)
                              ? formData.adlDependencies.filter(d => d !== adl)
                              : [...formData.adlDependencies, adl];
                            setFormData({...formData, adlDependencies: deps});
                          }}
                        >
                          {adl}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-teal-600" />
                    Labs & Supporting Data
                  </CardTitle>
                  <CardDescription>Laboratory values and clinical indicators</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="albumin">Serum Albumin (g/dL)</Label>
                      <Input
                        id="albumin"
                        type="number"
                        step="0.1"
                        data-testid="input-albumin"
                        value={formData.albumin}
                        onChange={(e) => setFormData({...formData, albumin: e.target.value})}
                        placeholder="e.g., 2.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="oxygenSaturation">O2 Saturation (%)</Label>
                      <Input
                        id="oxygenSaturation"
                        type="number"
                        data-testid="input-o2"
                        value={formData.oxygenSaturation}
                        onChange={(e) => setFormData({...formData, oxygenSaturation: e.target.value})}
                        placeholder="e.g., 92"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="weightLossPercent">Weight Loss % (6 mo)</Label>
                      <Input
                        id="weightLossPercent"
                        type="number"
                        data-testid="input-weight-loss"
                        value={formData.weightLossPercent}
                        onChange={(e) => setFormData({...formData, weightLossPercent: e.target.value})}
                        placeholder="e.g., -12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="hospitalizations">Hospitalizations (6 mo)</Label>
                      <Input
                        id="hospitalizations"
                        type="number"
                        data-testid="input-hospitalizations"
                        min={0}
                        value={formData.hospitalizations}
                        onChange={(e) => setFormData({...formData, hospitalizations: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Symptoms Present</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {symptomOptions.map(sym => (
                        <Badge
                          key={sym}
                          variant={formData.symptoms.includes(sym) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => {
                            const syms = formData.symptoms.includes(sym)
                              ? formData.symptoms.filter(s => s !== sym)
                              : [...formData.symptoms, sym];
                            setFormData({...formData, symptoms: syms});
                          }}
                        >
                          {sym}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button 
                      onClick={runEligibilityAssessment} 
                      disabled={loading}
                      className="w-full bg-teal-600 hover:bg-teal-700"
                      data-testid="btn-run-assessment"
                    >
                      {loading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Running Assessment...
                        </>
                      ) : (
                        <>
                          <Shield className="h-4 w-4 mr-2" />
                          Run Eligibility Assessment
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* LCD Criteria Tab */}
          <TabsContent value="lcd">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-teal-600" />
                  LCD Hospice Eligibility Criteria
                </CardTitle>
                <CardDescription>CMS Local Coverage Determination criteria by diagnosis category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Label>Select Diagnosis Category</Label>
                  <Select value={selectedCategory} onValueChange={fetchLCDCriteria}>
                    <SelectTrigger className="w-full max-w-md" data-testid="select-lcd-category">
                      <SelectValue placeholder="Choose a diagnosis category" />
                    </SelectTrigger>
                    <SelectContent>
                      {diagnosisCategories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {lcdCriteria && (
                  <div className="space-y-6 mt-6">
                    <div className="bg-teal-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-teal-800 mb-2">Prognosis Threshold</h3>
                      <p className="text-teal-700">{lcdCriteria.criteria?.prognosis_threshold || '6 months or less'}</p>
                    </div>

                    <div>
                      <h3 className="font-semibold text-slate-800 mb-3">Primary Indicators (Must meet at least 1)</h3>
                      <ul className="space-y-2">
                        {lcdCriteria.criteria?.primary_indicators?.map((indicator, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-teal-600 mt-1 flex-shrink-0" />
                            <span className="text-slate-700">{indicator}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="font-semibold text-slate-800 mb-3">Supporting Factors</h3>
                      <ul className="space-y-2">
                        {lcdCriteria.criteria?.supporting_factors?.map((factor, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Activity className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
                            <span className="text-slate-700">{factor}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results">
            {assessmentResult ? (
              <div className="space-y-6">
                {/* Eligibility Determination */}
                <Card className={assessmentResult.eligibility_determination?.is_eligible ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      {assessmentResult.eligibility_determination?.is_eligible ? (
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      ) : (
                        <AlertCircle className="h-6 w-6 text-orange-600" />
                      )}
                      Eligibility Determination
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Status</p>
                        <p className="text-lg font-semibold">
                          {assessmentResult.eligibility_determination?.is_eligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Confidence Level</p>
                        <p className="text-lg font-semibold capitalize">
                          {assessmentResult.eligibility_determination?.confidence_level}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Prognosis</p>
                        <p className="text-lg font-semibold">
                          {assessmentResult.eligibility_determination?.prognosis}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Mortality Risk */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="h-5 w-5 text-red-500" />
                      Mortality Risk at SOC
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Risk Score</p>
                        <p className="text-3xl font-bold text-slate-800">
                          {assessmentResult.mortality_risk_assessment?.overall_risk_score}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Risk Level</p>
                        <Badge className={getRiskBadgeColor(assessmentResult.mortality_risk_assessment?.risk_level)}>
                          {assessmentResult.mortality_risk_assessment?.risk_level?.toUpperCase()}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Estimated Prognosis</p>
                        <p className="text-lg font-semibold">
                          {assessmentResult.mortality_risk_assessment?.estimated_prognosis}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Assessment Date</p>
                        <p className="text-lg">
                          {new Date(assessmentResult.mortality_risk_assessment?.assessment_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {assessmentResult.mortality_risk_assessment?.risk_factors?.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-medium text-slate-700 mb-2">Risk Factors</h4>
                        <div className="flex flex-wrap gap-2">
                          {assessmentResult.mortality_risk_assessment.risk_factors.map((factor, idx) => (
                            <Badge key={idx} variant="outline" className="text-sm">
                              {factor.factor}: +{factor.points} pts
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* LCD Criteria Evaluation */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-500" />
                      LCD Criteria Evaluation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <p className="text-sm text-slate-500">Primary Criteria Met</p>
                        <p className="text-2xl font-bold text-slate-800">
                          {assessmentResult.lcd_criteria_evaluation?.primary_criteria_met}
                        </p>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <p className="text-sm text-slate-500">Supporting Factors Met</p>
                        <p className="text-2xl font-bold text-slate-800">
                          {assessmentResult.lcd_criteria_evaluation?.supporting_factors_met}
                        </p>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <p className="text-sm text-slate-500">Total Score</p>
                        <p className="text-2xl font-bold text-teal-600">
                          {assessmentResult.lcd_criteria_evaluation?.total_score}/100
                        </p>
                      </div>
                    </div>

                    {assessmentResult.lcd_criteria_evaluation?.primary_met_details?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium text-green-700 mb-2">Primary Criteria Met</h4>
                        <ul className="list-disc list-inside text-sm text-slate-600">
                          {assessmentResult.lcd_criteria_evaluation.primary_met_details.map((detail, idx) => (
                            <li key={idx}>{detail}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {assessmentResult.lcd_criteria_evaluation?.supporting_met_details?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-blue-700 mb-2">Supporting Factors Met</h4>
                        <ul className="list-disc list-inside text-sm text-slate-600">
                          {assessmentResult.lcd_criteria_evaluation.supporting_met_details.map((detail, idx) => (
                            <li key={idx}>{detail}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Care Plan Recommendations */}
                {assessmentResult.care_plan_recommendations && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="h-5 w-5 text-purple-500" />
                        Recommended Care Plan
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-slate-700 mb-2">Visit Frequency</h4>
                          <div className="space-y-1 text-sm">
                            {Object.entries(assessmentResult.care_plan_recommendations.visit_frequency || {}).map(([role, freq]) => (
                              <div key={role} className="flex justify-between">
                                <span className="capitalize text-slate-600">{role.replace('_', ' ')}</span>
                                <span className="font-medium">{freq}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-medium text-slate-700 mb-2">Recommended Equipment</h4>
                          <div className="flex flex-wrap gap-1">
                            {assessmentResult.care_plan_recommendations.equipment?.map((item, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">{item}</Badge>
                            ))}
                          </div>
                        </div>

                        <div className="md:col-span-2">
                          <h4 className="font-medium text-slate-700 mb-2">Goals of Care</h4>
                          <ul className="list-disc list-inside text-sm text-slate-600">
                            {assessmentResult.care_plan_recommendations.goals_of_care?.map((goal, idx) => (
                              <li key={idx}>{goal}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Audit Info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-slate-500" />
                      Audit Trail
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Assessment ID</p>
                        <p className="font-mono text-xs">{assessmentResult.id}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Created At</p>
                        <p>{new Date(assessmentResult.audit_trail?.created_at).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Methodology</p>
                        <p>{assessmentResult.audit_trail?.methodology}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Version</p>
                        <p>{assessmentResult.audit_trail?.version}</p>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-medium text-slate-700 mb-2">Compliance Attestation</h4>
                      <div className="flex flex-wrap gap-3">
                        {assessmentResult.compliance_attestation?.lcd_compliant && (
                          <Badge variant="outline" className="text-green-600 border-green-300">
                            <CheckCircle className="h-3 w-3 mr-1" /> LCD Compliant
                          </Badge>
                        )}
                        {assessmentResult.compliance_attestation?.documentation_complete && (
                          <Badge variant="outline" className="text-green-600 border-green-300">
                            <CheckCircle className="h-3 w-3 mr-1" /> Documentation Complete
                          </Badge>
                        )}
                        {assessmentResult.compliance_attestation?.face_to_face_required && (
                          <Badge variant="outline" className="text-orange-600 border-orange-300">
                            <Clock className="h-3 w-3 mr-1" /> F2F Required
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No assessment results yet. Run an assessment to see results here.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
